"use client";

import { useCallback, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { buildOAuthCallbackUrl } from "@/lib/auth-site";
import { cn } from "@/lib/utils";
import { authOAuthButton } from "@/lib/auth-ui";
import { useResetOnBfcache } from "@/lib/use-reset-on-bfcache";
import { setPendingAuth, type PendingAuthMode } from "@/lib/pending-auth";

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

type GoogleSignInButtonProps = {
  disabled?: boolean;
  /** Shown on the button; pick sign-up vs sign-in copy for the screen. */
  label: string;
  /** Merged onto the button (e.g. Figma-specific sizing). */
  buttonClassName?: string;
  /** Which modal to reopen if the user lands back on the site without
   * completing OAuth (back nav, provider error, etc.). Defaults to "login". */
  mode?: PendingAuthMode;
  /** Where to land the user after successful OAuth. Forwarded to the
   * /auth/callback handler so the homepage prompt-resume flow can run. */
  next?: string;
};

export function GoogleSignInButton({
  disabled = false,
  label,
  buttonClassName,
  mode = "login",
  next,
}: GoogleSignInButtonProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useResetOnBfcache(
    useCallback(() => {
      setBusy(false);
      setError(null);
    }, []),
  );

  async function handleClick() {
    setError(null);
    setBusy(true);

    const redirectTo = buildOAuthCallbackUrl(next);
    if (!redirectTo) {
      setError("Could not determine site URL. Set NEXT_PUBLIC_SITE_URL.");
      setBusy(false);
      return;
    }

    const supabase = createClient();
    // skipBrowserRedirect: true is critical — without it, supabase-js
    // navigates to data.url itself *during* this call, which means the
    // setPendingAuth() line below never runs and the modal can't reopen
    // when the user comes back via Back.
    // prompt=select_account stops Google from silently reusing the browser's
    // active session, so the user always picks which account to sign in with.
    const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        skipBrowserRedirect: true,
        queryParams: { prompt: "select_account" },
      },
    });

    if (oauthError) {
      setError(oauthError.message);
      setBusy(false);
      return;
    }

    if (data.url) {
      setPendingAuth(mode);
      try {
        const back = new URL(window.location.href);
        back.searchParams.set(mode, "1");
        window.history.pushState({}, "", back.toString());
      } catch {
        // ignore — fallback is sessionStorage
      }
      console.log("[auth] redirecting to Google OAuth", { mode });
      window.location.assign(data.url);
      return;
    }

    setError("Could not start Google sign-in. Try again.");
    setBusy(false);
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        className={cn(authOAuthButton, "rounded-full", buttonClassName)}
        onClick={() => void handleClick()}
        disabled={disabled || busy}
      >
        <GoogleMark />
        {busy ? "Redirecting…" : label}
      </button>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
    </div>
  );
}
